<script setup lang="ts">
import { ref, reactive } from 'vue';
import i18n from '@/i18n';
import every from 'lodash/every';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { LANG_ENGLISH, LANG_FRENCH } from '@/constants';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { PromosEnum } from '@/store/enums/PromosEnum';
import { usePromosStore } from '@/store/PromosStore';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputTextAera from 'primevue/textarea';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Toast from 'primevue/toast';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import FormPromo from '@/components/promos/FormPromo.vue';
import UploadMultipleImages from './UploadMultipleImages.vue';

const { canEditPromo } = usePromoComposable();

const currentLanguage = i18n.global.locale.value;
const promosStore = usePromosStore();
const confirm = useConfirm();
const toast = useToast();
const isShowError = ref(false);
const formPromoInfo = ref([]);
const multipleUploadTopEn = ref();
const multipleUploadBottomEn = ref();
const multipleUploadTopFr = ref();
const multipleUploadBottomFr = ref();
const visiblePreviewFormPromo = ref(false);
const headerPreviewFormPromo = ref('');

const initPayload = () => {
  return {
    promotionType: PromosEnum.promotionType.FORM_PROMOS,
    qType: '',
    sk: '',
    topTitleEn: '',
    topTitleFr: '',
    topDescEn: '',
    topDescFr: '',
    topImgUrlEn: '',
    topImgTitleEn: '',
    topImgUrlFr: '',
    topImgTitleFr: '',
    topTCEn: '',
    topTCFr: '',
    bottomTitleEn: '',
    bottomTitleFr: '',
    bottomDescEn: '',
    bottomDescFr: '',
    bottomImgUrlEn: '',
    bottomImgTitleEn: '',
    bottomImgUrlFr: '',
    bottomImgTitleFr: '',
    bottomTCEn: '',
    bottomTCFr: '',
    topImageEn: {
      desktop: '',
      mobile: '',
      tablet: '',
      widescreen: ''
    },
    topImageFr: {
      desktop: '',
      mobile: '',
      tablet: '',
      widescreen: ''
    },
    bottomImageEn: {
      desktop: '',
      mobile: '',
      tablet: '',
      widescreen: ''
    },
    bottomImageFr: {
      desktop: '',
      mobile: '',
      tablet: '',
      widescreen: ''
    }
  };
};

const payload = reactive({ ...initPayload() });
const objectCopy = ref();
const isLoading = ref(false);
const dataPreview = reactive([]);
const selectedType = ref('');
const typeForm = PromosEnum.quoteTypeFormPromo;
const rules = ref({});
const notRequired = ['sk'];

for (const property in payload) {
  if (notRequired.indexOf(property) === -1) {
    rules.value[property] = {
      required: helpers.withMessage(i18n.global.t('validate.required'), required)
    };
  }
}
const v$ = useVuelidate(rules, payload);

const promoDataType = {
  top: 'top',
  bottom: 'bottom'
};
const getDataPreview = (promo, type, language) => {
  const fields = [
    {
      key: 'title',
      field: 'Title'
    },
    {
      key: 'description',
      field: 'Desc'
    },
    {
      key: 'TC',
      field: 'TC'
    },
    {
      key: 'renderImage',
      field: 'Image'
    }
  ];
  const data = {};
  fields.forEach((field) => {
    data[field.key] = promo[`${type}${field.field}${language.replace(/^\w/, (c) => c.toUpperCase())}`];
  });
  return data;
};

const setDataPreview = (promoData, language) => {
  dataPreview.length = 0;
  dataPreview.push(getDataPreview(promoData, promoDataType.top, language));
  dataPreview.push(getDataPreview(promoData, promoDataType.bottom, language));
};
const selectTypeForm = async (e) => {
  multipleUploadTopEn.value.resetFileUpload();
  multipleUploadBottomEn.value.resetFileUpload();
  multipleUploadTopFr.value.resetFileUpload();
  multipleUploadBottomFr.value.resetFileUpload();
  await getFormPromo();
  isShowError.value = false;
  const type = e.target.textContent;
  selectedType.value = type;
  payload.qType = typeForm[type];
  const findPromo = formPromoInfo.value.find((item) => item.qType == typeForm[type]);
  const data = findPromo || initPayload();
  for (const property in payload) {
    payload[property] = data[property];
  }
  payload.qType = typeForm[type];
  payload.promotionType = PromosEnum.promotionType.FORM_PROMOS;
  objectCopy.value = cloneDeep(payload);
  setDataPreview(payload, currentLanguage);
};

const handleSubmit = async () => {
  const isEmptyPayload = every(omit(cloneDeep(payload), ['sk', 'promotionType', 'qType', 'topImageEn', 'topImageFr', 'bottomImageEn', 'bottomImageFr']), isEmpty);
  if (isEmptyPayload) {
    toast.add({ severity: 'error', summary: '', detail: i18n.global.t('validate.at_least_field_required'), life: 5000 });
    return;
  }
  promosStore
    .submitPromos(payload, PromosEnum.path.FORM_PROMOS)
    .then(() => {
      handleAfterFetch(i18n.global.t('notification.save_successfully'));
    })
    .finally(() => (isShowError.value = false));
};

const handleDelete = async () => {
  confirm.require({
    message: i18n.global.t('promos.labels.confirm_delete'),
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    accept: async () => {
      await promosStore.deletePromos(payload.sk, PromosEnum.path.FORM_PROMOS);
      handleAfterFetch(i18n.global.t('notification.save_successfully'));
    }
  });
};
const handleAfterFetch = (msg: string) => {
  toast.add({ severity: 'success', summary: '', detail: msg, life: 3000 });
  const emptyPayload = initPayload();
  for (const property in payload) {
    payload[property] = emptyPayload[property];
  }
  payload.promotionType = PromosEnum.promotionType.FORM_PROMOS;
  selectedType.value = null;
  multipleUploadTopEn.value.resetFileUpload();
  multipleUploadBottomEn.value.resetFileUpload();
  multipleUploadTopFr.value.resetFileUpload();
  multipleUploadBottomFr.value.resetFileUpload();
  getFormPromo();
  setDataPreview(payload, currentLanguage);
};

const handleCancel = () => {
  if (!selectedType.value) {
    return;
  }
  const oldData = objectCopy.value ? cloneDeep(objectCopy.value) : initPayload();
  for (const key in payload) {
    payload[key] = oldData[key];
  }
  setDataPreview(payload, currentLanguage);
  getFormPromo();
};

const getFormPromo = async () => {
  isLoading.value = true;
  return promosStore
    .getFormPromo()
    .then((res) => {
      formPromoInfo.value = res;
    })
    .finally(() => {
      isLoading.value = false;
    });
};
getFormPromo();

const onUploadDataTopFormPromoEn = (dataImage) => {
  payload.topImageEn = { ...dataImage };
};

const onUploadDataBottomFormPromoEn = (dataImage) => {
  payload.bottomImageEn = { ...dataImage };
};

const onUploadDataTopFormPromoFr = (dataImage) => {
  payload.topImageFr = { ...dataImage };
};

const onUploadDataBottomFormPromoFr = (dataImage) => {
  payload.bottomImageFr = { ...dataImage };
};

const handleRemovePreviewImgFormPromo = (key, payloadImage) => {
  payloadImage[key] = '';
};

const openDialogPreviewFormPromo = (language) => {
  setDataPreview(payload, language);
  visiblePreviewFormPromo.value = true;
  if (language === LANG_ENGLISH) {
    headerPreviewFormPromo.value = i18n.global.t('promos.labels.preview_english');
    return;
  }
  headerPreviewFormPromo.value = i18n.global.t('promos.labels.preview_french');
};
</script>

<template>
  <div class="form-promo-admin">
    <Toast position="top-left" group="tl" />
    <Splitter v-if="payload">
      <SplitterPanel>
        <Splitter layout="vertical">
          <SplitterPanel class="ml-4 hide-gutter">
            <div v-if="typeForm">
              <span v-for="(type, index) in Object.keys(typeForm)" :key="index">
                <Button :id="`${type}-btn`" :class="[selectedType === type || !selectedType ? 'opacity-100' : 'opacity-60']" :label="$t(`form_promos.${type.toLowerCase()}`)" class="mt-3 mr-2" @click="selectTypeForm($event)" />
              </span>
              <div class="text-red-500" v-if="isShowError && v$.qType.$invalid">
                {{ v$.qType.required.$message }}
              </div>
            </div>
          </SplitterPanel>
          <SplitterPanel>
            <LoadingSpinner v-if="isLoading" />
            <Splitter layout="vertical">
              <SplitterPanel class="p-5">
                <h5>{{ $t('promos.labels.en_top') }}</h5>
                <div class="flex flex-col">
                  <span class="p-float-label mt-5">
                    <InputText id="form-promo-topImgTitleEn" :disabled="!selectedType || !canEditPromo" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.topImgTitleEn" />
                    <label for="form-promo-topImgTitleEn">{{ $t('promos.labels.image_title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.topImgTitleEn.$invalid">
                    {{ v$.topImgTitleEn.required.$message }}
                  </span>
                  <div class="mt-5">
                    <UploadMultipleImages
                      ref="multipleUploadTopEn"
                      :disabled="!selectedType"
                      @handleUploadData="onUploadDataTopFormPromoEn($event)"
                      :listUrlPreview="payload.topImageEn"
                      @handleRemoveImgReview="(key) => handleRemovePreviewImgFormPromo(key, payload.topImageEn)"
                    ></UploadMultipleImages>
                  </div>
                  <span class="p-float-label mt-5">
                    <InputText id="form-promo-topTitleEn" :disabled="!selectedType || !canEditPromo" type="text" maxlength="100" class="mt-1 mb-1 w-full" v-model.trim="payload.topTitleEn" />
                    <label for="form-promo-topTitleEn">{{ $t('promos.labels.title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.topTitleEn.$invalid">
                    {{ v$.topTitleEn.required.$message }}
                  </span>
                  <span class="p-float-label mt-5">
                    <InputTextAera id="form-promo-topDescEn" :disabled="!selectedType || !canEditPromo" class="mb-1 w-full" v-model.trim="payload.topDescEn" :autoResize="true" rows="5" cols="30" />
                    <label for="form-promo-topDescEn">{{ $t('promos.labels.description') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.topDescEn.$invalid">
                    {{ v$.topDescEn.required.$message }}
                  </span>
                  <span class="p-float-label mt-5">
                    <InputTextAera :disabled="!selectedType || !canEditPromo" id="form-promo-topTermsEn" class="mb-1 w-full" v-model.trim="payload.topTCEn" :autoResize="true" rows="5" cols="30" />
                    <label for="form-promo-topTermsEn">{{ $t('promos.labels.term') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.topTCEn.$invalid">
                    {{ v$.topTCEn.required.$message }}
                  </span>
                </div>
              </SplitterPanel>
              <SplitterPanel class="p-5">
                <h5>{{ $t('promos.labels.en_bottom') }}</h5>
                <div class="flex flex-col">
                  <span class="p-float-label mt-5">
                    <InputText id="form-promo-bottomImgTitleEn" :disabled="!selectedType || !canEditPromo" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.bottomImgTitleEn" />
                    <label for="form-promo-bottomImgTitleEn">{{ $t('promos.labels.image_title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.bottomImgTitleEn.$invalid">
                    {{ v$.bottomImgTitleEn.required.$message }}
                  </span>
                  <div class="mt-5">
                    <UploadMultipleImages
                      ref="multipleUploadBottomEn"
                      @handleUploadData="onUploadDataBottomFormPromoEn($event)"
                      :disabled="!selectedType"
                      :listUrlPreview="payload.bottomImageEn"
                      @handleRemoveImgReview="(key) => handleRemovePreviewImgFormPromo(key, payload.bottomImageEn)"
                    ></UploadMultipleImages>
                  </div>
                  <span class="p-float-label mt-5">
                    <InputText id="form-promo-bottomTitleEn" :disabled="!selectedType || !canEditPromo" type="text" maxlength="100" class="mt-1 mb-1 w-full" v-model.trim="payload.bottomTitleEn" />
                    <label for="form-promo-bottomTitleEn">{{ $t('promos.labels.title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.bottomTitleEn.$invalid">
                    {{ v$.bottomTitleEn.required.$message }}
                  </span>
                  <span class="p-float-label mt-5">
                    <InputTextAera id="form-promo-bottomDescEn" :disabled="!selectedType || !canEditPromo" class="mb-1 w-full" v-model.trim="payload.bottomDescEn" :autoResize="true" rows="5" cols="30" />
                    <label for="form-promo-bottomDescEn">{{ $t('promos.labels.description') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.bottomDescEn.$invalid">
                    {{ v$.bottomDescEn.required.$message }}
                  </span>
                  <span class="p-float-label mt-5">
                    <InputTextAera :disabled="!selectedType || !canEditPromo" id="form-promo-bottomTermsEn" class="mb-1 w-full" v-model.trim="payload.bottomTCEn" :autoResize="true" rows="5" cols="30" />
                    <label for="form-promo-bottomTermsEn">{{ $t('promos.labels.term') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.bottomTCEn.$invalid">
                    {{ v$.bottomTCEn.required.$message }}
                  </span>
                  <div class="flex justify-start">
                    <Button id="preview-en-btn" :disabled="!selectedType" :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3 mr-2" @click="openDialogPreviewFormPromo(LANG_ENGLISH)" />
                  </div>
                </div>
              </SplitterPanel>
            </Splitter>
            <Splitter layout="vertical">
              <SplitterPanel class="p-5">
                <h5>{{ $t('promos.labels.fr_top') }}</h5>
                <div class="flex flex-col">
                  <span class="p-float-label mt-5">
                    <InputText id="form-promo-topImgTitleFr" :disabled="!selectedType || !canEditPromo" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.topImgTitleFr" />
                    <label for="form-promo-topImgTitleFr">{{ $t('promos.labels.image_title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.topImgTitleFr.$invalid">
                    {{ v$.topImgTitleFr.required.$message }}
                  </span>
                  <div class="mt-5">
                    <UploadMultipleImages
                      ref="multipleUploadTopFr"
                      @handleUploadData="onUploadDataTopFormPromoFr($event)"
                      :disabled="!selectedType"
                      :listUrlPreview="payload.topImageFr"
                      @handleRemoveImgReview="(key) => handleRemovePreviewImgFormPromo(key, payload.topImageFr)"
                    ></UploadMultipleImages>
                  </div>
                  <span class="p-float-label mt-5">
                    <InputText id="form-promo-topTitleFr" :disabled="!selectedType || !canEditPromo" type="text" maxlength="100" class="mt-1 mb-1 w-full" v-model.trim="payload.topTitleFr" />
                    <label for="form-promo-topTitleFr">{{ $t('promos.labels.title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.topTitleFr.$invalid">
                    {{ v$.topTitleFr.required.$message }}
                  </span>
                  <span class="p-float-label mt-5">
                    <InputTextAera id="form-promo-topDescFr" :disabled="!selectedType || !canEditPromo" class="mb-1 w-full" v-model.trim="payload.topDescFr" :autoResize="true" rows="5" cols="30" />
                    <label for="form-promo-topDescFr">{{ $t('promos.labels.description') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.topDescFr.$invalid">
                    {{ v$.topDescFr.required.$message }}
                  </span>
                  <span class="p-float-label mt-5">
                    <InputTextAera :disabled="!selectedType || !canEditPromo" id="side-promo-topTermsFr" class="mb-1 w-full" v-model.trim="payload.topTCFr" :autoResize="true" rows="5" cols="30" />
                    <label for="side-promo-topTermsFr">{{ $t('promos.labels.term') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.topTCFr.$invalid">
                    {{ v$.topTCFr.required.$message }}
                  </span>
                </div>
              </SplitterPanel>
              <SplitterPanel class="p-5">
                <h5>{{ $t('promos.labels.fr_bottom') }}</h5>
                <div class="flex flex-col">
                  <span class="p-float-label mt-5">
                    <InputText id="form-promo-bottomImgTitleFr" :disabled="!selectedType || !canEditPromo" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.bottomImgTitleFr" />
                    <label for="form-promo-bottomImgTitleFr">{{ $t('promos.labels.image_title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.bottomImgTitleFr.$invalid">
                    {{ v$.bottomImgTitleFr.required.$message }}
                  </span>
                  <div class="mt-5">
                    <UploadMultipleImages
                      ref="multipleUploadBottomFr"
                      :disabled="!selectedType"
                      @handleUploadData="onUploadDataBottomFormPromoFr($event)"
                      :listUrlPreview="payload.bottomImageFr"
                      @handleRemoveImgReview="(key) => handleRemovePreviewImgFormPromo(key, payload.bottomImageFr)"
                    ></UploadMultipleImages>
                  </div>
                  <span class="p-float-label mt-5">
                    <InputText id="form-promo-bottomTitleFr" :disabled="!selectedType || !canEditPromo" type="text" maxlength="100" class="mt-1 mb-1 w-full" v-model.trim="payload.bottomTitleFr" />
                    <label for="form-promo-bottomTitleFr">{{ $t('promos.labels.title') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.bottomTitleFr.$invalid">
                    {{ v$.bottomTitleFr.required.$message }}
                  </span>
                  <span class="p-float-label mt-5">
                    <InputTextAera id="form-promo-bottomDescFr" :disabled="!selectedType || !canEditPromo" class="mb-1 w-full" v-model.trim="payload.bottomDescFr" :autoResize="true" rows="5" cols="30" />
                    <label for="form-promo-bottomDescFr">{{ $t('promos.labels.description') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.bottomDescFr.$invalid">
                    {{ v$.bottomDescFr.required.$message }}
                  </span>
                  <span class="p-float-label mt-5">
                    <InputTextAera :disabled="!selectedType || !canEditPromo" id="side-promo-bottomTermsFr" class="mb-1 w-full" v-model.trim="payload.bottomTCFr" :autoResize="true" rows="5" cols="30" />
                    <label for="side-promo-bottomTermsFr">{{ $t('promos.labels.term') }}</label>
                  </span>
                  <span class="text-red-500" v-if="isShowError && v$.bottomTCFr.$invalid">
                    {{ v$.bottomTCFr.required.$message }}
                  </span>
                  <div class="flex justify-start">
                    <Button id="preview-fr-btn" :disabled="!selectedType" :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3 mr-2" @click="openDialogPreviewFormPromo(LANG_FRENCH)" />
                  </div>
                </div>
              </SplitterPanel>
            </Splitter>
          </SplitterPanel>
          <SplitterPanel v-if="canEditPromo" class="p-3 text-center">
            <Button id="promos-save-btn" :disabled="!selectedType" :label="$t('promos.save')" icon="pi pi-check" class="p-button-secondary mr-2" @click="handleSubmit()" />
            <Button id="promos-delete-btn" :disabled="!payload.sk" :label="$t('promos.delete')" icon="pi pi-trash" class="mr-2" @click="handleDelete()" />
            <Button id="promos-cancel-btn" :label="$t('promos.labels.cancel')" icon="pi pi-times" @click="handleCancel()" />
          </SplitterPanel>
        </Splitter>
      </SplitterPanel>
      <SplitterPanel class="form-promo-side">
        <FormPromo :value="dataPreview"></FormPromo>
      </SplitterPanel>
    </Splitter>
    <Dialog :header="headerPreviewFormPromo" v-model:visible="visiblePreviewFormPromo" :modal="true" class="w-3">
      <FormPromo :value="dataPreview"></FormPromo>
    </Dialog>
  </div>
</template>
